import type { FC } from 'react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { call } from 'redux-saga/effects';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import HookFormDevtools from '@wix/da-shared-react/pkg/utils/hookform/Devtools';
import {
  PapiDeviation,
  PapiEditorText,
  PapiGallection,
} from '@wix/da-papi-types';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { BiData, BuyCoreButtonBiEvent } from '@wix/da-bi/pkg/events';
import { Url, URLS } from '@wix/da-url';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useMeasureElementCookie } from '@wix/da-hooks/pkg/useMeasureElement/useMeasureElementCookie';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import BooleanInput from '@wix/da-ds/pkg/formControls/dsPresets/BooleanInput';
import AddCoverImageIcon from '@wix/da-ds/pkg/Icons/24x24/AddCoverImage';
import EditIcon from '@wix/da-ds/pkg/Icons/24x24/Edit';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import IconButtonWithBackground from '@wix/da-ds/pkg/Buttons/IconButton/IconButtonWithBackground';
import MediaSelector from '@wix/da-shared-react/pkg/MediaPopup/MediaSelector';
import { MediaSourceType } from '@wix/da-shared-react/pkg/MediaPopup/types';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import Dumbo from '@wix/da-shared-react/pkg/DeviationViews/Dumbo';
import yupResolver from '@wix/da-shared-react/pkg/utils/yupResolver';
import LoadingScreenModal from '@wix/da-shared-react/pkg/LoadingScreenModal';
import { useValidation } from './validation';
import GallectionEditor from '@wix/da-shared-react/pkg/GallectionEditor';
import { EditorApi } from '@wix/da-editor-tiptap/pkg/components/Editor/types';
import FormElement from '@wix/da-ds/pkg/formControls/layouts/FormElement';
import InputLabelText from '@wix/da-ds/pkg/formControls/lego/InputLabelText';
import LegacyDescriptionEditor from '@wix/da-submit-shared/pkg/fields/DescriptionField/_partials/LegacyDescriptionEditor';
import { emptyDocument } from '@wix/da-editor-tiptap/pkg/components/utils/helpers';
import { PremiumFolderType } from '@wix/da-shared-react/pkg/types/gallection';
import PremiumForm from '@wix/da-shared-react/pkg/forms/PremiumForm';
import PriceInputWithEarnings from '@wix/da-shared-react/pkg/DollarPriceInput/variants/PriceInputWithEarnings';
import { dollarsToPoints } from '@wix/da-shared-react/pkg/utils/points';
import {
  CoreLevel,
  SessionContext,
} from '@wix/da-react-context/pkg/publicSession';

import useBookmarksStream from '../../hooks/useBookmarksStream';
import { FolderType } from '../../types/folders';

import omit from 'lodash/omit';

import s from './FolderModal.scss';

const COVER_HEIGHT = 150;
const DEFAULT_PRICE = 5;

export interface Props {
  folderId?: number;
  featuredFolderId?: number;
  selectedFolder?: PapiGallection;

  isBusy?: boolean;
  folderType: FolderType;
  coreLevel: CoreLevel;
  fetchFolder: (onFetchSuccess: (folder: PapiGallection) => void) => void;
  saveFolder: (data: any, onSaveSuccess: (folderId: number) => void) => void;
  onSaveSuccess?: (folderId: number) => void;
  requestClose: () => void;
}

const FolderModal: FC<Props> = ({
  folderId,
  featuredFolderId,
  selectedFolder,
  folderType,
  isBusy,
  coreLevel,
  fetchFolder,
  saveFolder,
  onSaveSuccess,
  requestClose,
}) => {
  const [folder, setFolder] = useState<PapiGallection>();
  // gallection.isBookmarked does not seem to be a consistent way of
  // determining whether the gallection has been bookmarked or not, so we check
  // the bookmarks stream directly
  const { items: bookmarks = [] } = useBookmarksStream();
  const isBookmarked = folderId && bookmarks.some(b => b.folderId === folderId);
  const { hasCoreMembership } = useContext(SessionContext);
  const editorApi = useRef<EditorApi | undefined>(undefined);
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const validationSchema = useValidation(coreLevel);
  const premiumFolderType =
    folder?.premiumFolderData?.type || PremiumFolderType.Regular;
  const premiumFolderDollarPrice =
    folder?.premiumFolderData?.dollarPrice || DEFAULT_PRICE;
  const formContext = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      title: '',
      price: premiumFolderDollarPrice,
      // folderType is actually premiumFolderType, the name collision comes
      // from the PremiumForm component
      folderType: premiumFolderType,
      type: folderType,
      description: undefined,
      cover_deviationid: undefined,
    },
  });
  const isGallery = folderType == 'gallery';
  const selectedFolderId = selectedFolder?.folderId || 0;
  const isSubfolder = isGallery
    ? selectedFolderId > 0
    : selectedFolderId !== featuredFolderId;
  const isCreate = !folderId;
  const isEdit = !isCreate;
  const isFeaturedFolder = isEdit && folder?.folderId === featuredFolderId;

  const showPriceInput = isEdit && premiumFolderType === PremiumFolderType.Paid;

  // reset scroll to top due to autoFocus on open on Folder Name field
  // which affects mobile due to limited screen height
  const onClose = () => {
    isMobile &&
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: -1 });
      });
    requestClose();
  };

  const { control, errors, setValue, handleSubmit, register, reset } =
    formContext;

  useEffect(() => {
    register('type');
    setValue('type', folderType);
  }, [register, setValue, folderType]);

  useEffect(() => {
    if (showPriceInput) {
      // if PremiumForm is not shown, we need to register folderType
      register('folderType');
    }
  }, [register, setValue, premiumFolderType, showPriceInput]);

  useEffect(() => {
    reset({
      price: premiumFolderDollarPrice,
      folderType: premiumFolderType,
    });
  }, [folder, reset, premiumFolderDollarPrice, premiumFolderType]);

  const { ref: coverWrapperRef, dimensions: measuredContainer } =
    useMeasureElementCookie(undefined, { cookieDim: 'width' });
  const coverWidth = measuredContainer.width - 2;
  const [coverThumb, setCoverThumb] = useState<PapiDeviation>();
  const [isCoverSelectorVisible, setShowCoverSelector] = useState(false);
  const showCoverSelector = useCallback(() => setShowCoverSelector(true), []);
  const hideCoverSelector = useCallback(() => setShowCoverSelector(false), []);
  const onCoverSelect = useCallback(
    (items: PapiDeviation[]) => {
      const selectedDeviation = items[0];
      if (selectedDeviation) {
        setValue('cover_deviationid', selectedDeviation.deviationId);
        setCoverThumb(selectedDeviation);
      }
      hideCoverSelector();
    },
    [setValue, hideCoverSelector]
  );

  const showPremiumFolderForm = isGallery && isCreate && !isSubfolder;

  const { watch } = formContext;
  const selectedFolderType = watch('folderType');

  const isPremiumGallerySelected = selectedFolderType === 'paid';
  const isWatchersOnlySelected = selectedFolderType === 'watchers';
  const showUpsellButton =
    isCreate &&
    !hasCoreMembership &&
    (isSubfolder || isPremiumGallerySelected || isWatchersOnlySelected);

  const onSubmit = useCallback(
    data => {
      if (
        data.description &&
        data.description.html &&
        data.description.html.type &&
        data.description.html.type === 'writer'
      ) {
        // Never send writer to backend
        data = omit(data, 'description');
      }
      data.premium_type =
        data.folderType === PremiumFolderType.Regular
          ? undefined
          : data.folderType;

      data.points_price =
        data.folderType === PremiumFolderType.Paid
          ? dollarsToPoints(data.price)
          : undefined;

      data = omit(data, 'folderType', 'price');

      saveFolder(data, function* (savedFolderId: number) {
        if (onSaveSuccess) {
          yield call(onSaveSuccess, savedFolderId);
        }
        onClose();
      });
    },
    [saveFolder, onSaveSuccess, onClose, showPriceInput]
  );

  // If we ever need more flexibility we should move this state to redux.
  // For now this works and requires less boilerplate
  useEffect(() => {
    if (folderId && !folder) {
      fetchFolder(f => {
        setFolder(f);
        if (f.thumb) {
          setCoverThumb(f.thumb);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (folderId && !folder) {
    return <LoadingScreenModal />;
  }

  const addToShortcutsCheckbox = (
    <Controller
      name="addToShortcuts"
      control={control}
      defaultValue={isBookmarked}
      render={({ onChange, value, name }) => (
        <BooleanInput
          id="addToShortcuts"
          label={t('studio.modals.folder.label.addToShortcuts')}
          type="checkbox"
          onChange={onChange}
          value={value}
          name={name}
          noBottomSpacing
        />
      )}
    />
  );

  return (
    <FormProvider {...formContext}>
      <Modal
        className={s['wrapper']}
        layoutClassName={s['layout']}
        contentClassName={s['content']}
        heading={t(
          `studio.modals.folder.title.${isEdit ? 'edit' : 'new'}.var.${folderType}${isGallery && isSubfolder ? '.subfolder' : ''}`
        )}
        footerLeft={[addToShortcutsCheckbox]}
        footerRight={[
          <TextButton
            key="footer-cancel"
            size="large"
            variant="main"
            onClick={onClose}
            type="button"
          >
            {t('studio.modals.folder.button.cancel')}
          </TextButton>,
          showUpsellButton && (
            <BrandedButton
              size="large"
              variant="core-orange"
              href={Url.to(URLS.CORE_MEMBERSHIP)}
              biData={BiData<BuyCoreButtonBiEvent>({
                evid: 375,
                referral_info: ReferralType.ADD_SUBFOLDER,
              })}
            >
              {t('common.upgradeToCoreCTA')}
            </BrandedButton>
          ),
          !showUpsellButton && (
            <BrandedButton
              key="footer-save"
              size="large"
              variant="brand-green"
              onClick={() => {
                if (
                  !editorApi.current?.getStateForSave &&
                  folder?.richDescription?.html.markup
                ) {
                  try {
                    const markup = JSON.parse(
                      folder?.richDescription?.html.markup ?? ''
                    );
                    setValue('description', markup);
                  } catch (e) {}
                } else {
                  setValue(
                    'description',
                    editorApi.current?.getStateForSave() ?? emptyDocument()
                  );
                }
                handleSubmit(onSubmit)();
              }}
              loading={isBusy}
              disabled={isBusy}
              type="submit"
            >
              {t(`studio.modals.folder.button.${folder ? 'update' : 'create'}`)}
            </BrandedButton>
          ),
        ]}
        onRequestClose={onClose}
        showHeaderBorder
        showFooterBorder
        shouldCloseOnOverlayClick
        isOpen
        isFullscreen={isMobile}
        withScrollableContentWrapper
      >
        <Controller
          control={control}
          name="cover_deviationid"
          defaultValue={coverThumb ? coverThumb.deviationId : ''}
          render={() => (
            <div className={s['cover-wrapper']} ref={coverWrapperRef}>
              {!coverThumb && (
                <PlainButtonOrLink
                  className={s['cover-button-add']}
                  onClick={showCoverSelector}
                  type="button"
                >
                  <AddCoverImageIcon className={s['cover-button-icon']} />
                  {t('mediapopup.setGallectionCover')}
                </PlainButtonOrLink>
              )}
              {coverThumb && (
                <Dumbo
                  useCoverMode
                  width={coverWidth}
                  height={COVER_HEIGHT}
                  deviation={coverThumb}
                />
              )}
              {coverThumb && (
                <div className={s['cover-buttons-wrapper']}>
                  <IconButtonWithBackground
                    size="small"
                    icon={EditIcon}
                    className={s['cover-button-edit']}
                    onClick={showCoverSelector}
                    type="button"
                    aria-label={t('mediapopup.changeGallectionCover')}
                  />
                </div>
              )}
            </div>
          )}
        />
        <Controller
          control={control}
          name="name"
          defaultValue={folder?.name ?? ''}
          render={({ onChange, value, name, ref }) => (
            <TextInput
              id="name-field"
              label={t('studio.modals.folder.label.name')}
              placeholder={t('studio.modals.folder.placeholder.name')}
              validationResult={errors[name] ? 'error' : undefined}
              details={errors[name]?.message}
              name={name}
              value={value}
              ref={ref}
              onChange={onChange}
              slimBottomSpacing
              disabled={isGallery && isFeaturedFolder}
              autoFocus
            />
          )}
        />
        <Controller
          control={control}
          name="description"
          defaultValue={folder?.richDescription ?? ''}
          render={({ onChange, value, name, ref }) => {
            const isLegacyDescription =
              (typeof value === 'string' && value !== '') ||
              ((value as PapiEditorText)?.html?.type === 'writer' &&
                value.html.markup !== '');
            return (
              <FormElement
                inputId={'description'}
                noBottomSpacing={!showPriceInput && !showPremiumFolderForm}
                label={
                  <InputLabelText>
                    {t('studio.modals.folder.label.description')}
                  </InputLabelText>
                }
              >
                {isLegacyDescription && (
                  <LegacyDescriptionEditor
                    descriptionHtml={{
                      features: '',
                      type: 'writer',
                      markup:
                        typeof value === 'string' ? value : value.html.markup,
                    }}
                    onConvertToDraft={v => {
                      const editorContent: PapiEditorText = {
                        excerpt: '',
                        html: {
                          features: '',
                          type: 'tiptap',
                          markup: JSON.stringify(v),
                        },
                      };
                      setValue('description', editorContent);
                    }}
                  />
                )}
                {!isLegacyDescription && (
                  <GallectionEditor
                    initialContent={value}
                    onInitialContentReady={api => (editorApi.current = api)}
                    compact
                  />
                )}
              </FormElement>
            );
          }}
        />

        {showPremiumFolderForm && (
          <FormElement
            inputId={'description'}
            label={
              <InputLabelText>
                {t('studio.modals.folder.label.premiumGallery')}
              </InputLabelText>
            }
          >
            <PremiumForm fullWidth />
          </FormElement>
        )}

        {showPriceInput && (
          <Controller
            control={control}
            name="price"
            render={({ onChange, value, name }) => {
              return (
                <div className={s['premium-selected']}>
                  <PriceInputWithEarnings
                    label={t('mediapopup.newFolderPopup.a11y.priceInputLabel')}
                    className={s['price-input']}
                    error={errors[name]?.message}
                    isRequired
                    placeholder={DEFAULT_PRICE}
                    productType="gallery"
                    onChange={onChange}
                    value={value}
                    name={name}
                  />
                </div>
              );
            }}
          />
        )}
        <HookFormDevtools control={control} />

        {isCoverSelectorVisible && (
          <MediaSelector
            mediaSourceTypes={[MediaSourceType.GALLERY, MediaSourceType.STASH]}
            allowedDeviationTypes={[DeviationTypes.IMAGE]}
            allowGifs={false}
            allowPremium={false}
            allowMature={false}
            minDeviationWidth={coverWidth}
            minDeviationHeight={COVER_HEIGHT}
            onClose={() => setShowCoverSelector(false)}
            onSelect={onCoverSelect}
          />
        )}
      </Modal>
    </FormProvider>
  );
};

FolderModal.displayName = 'FolderModal';

export default FolderModal;
